import { toast } from 'sonner'
import type { BadgeVariant } from '@/components/ui'
import type { OrganizationDashboardRosterStatus } from '@/types/dashboard.types'

const compactNumber = new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 })

/** Locale number; `undefined`/`NaN` from an older server render as 0, never "NaN". */
export function formatNumber(value: number | null | undefined): string {
  return compactNumber.format(Number.isFinite(value) ? (value as number) : 0)
}

/** "4h 46m". Rounds first: running sessions report fractional minutes. */
export function formatMinutes(minutes: number): string {
  const total = Math.round(minutes)
  if (total < 60) return `${total}m`
  const h = Math.floor(total / 60)
  const m = total % 60
  return m === 0 ? `${h}h` : `${h}h ${m}m`
}

export function formatPct(pct: number | null | undefined): string {
  return pct === null || pct === undefined ? '—' : `${pct}%`
}

export function formatDay(iso: string): string {
  if (!iso) return ''
  try {
    return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(
      new Date(iso)
    )
  } catch {
    return iso
  }
}

/** Wall-clock time in the dashboard's timezone, e.g. "09:02 AM". */
export function formatClock(iso: string | null | undefined, timezone?: string): string {
  if (!iso) return '—'
  try {
    return new Intl.DateTimeFormat(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: timezone || undefined,
    }).format(new Date(iso))
  } catch {
    return '—'
  }
}

export function formatUpdated(iso: string | undefined, timezone: string | undefined): string {
  if (!iso) return ''
  try {
    return new Intl.DateTimeFormat(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: timezone || undefined,
    }).format(new Date(iso))
  } catch {
    return ''
  }
}

/** "Sep 28 – Oct 4" (or "Oct 5 – 11" within one month) for a Mon–Sun list of `YYYY-MM-DD` dates. */
export function formatWeekRange(dates: string[]): string {
  if (dates.length === 0) return ''
  const first = new Date(`${dates[0]}T00:00:00Z`)
  const last = new Date(`${dates[dates.length - 1]}T00:00:00Z`)
  const dayMonth = new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  })
  if (first.getUTCMonth() === last.getUTCMonth()) {
    return `${dayMonth.format(first)} – ${last.getUTCDate()}`
  }
  return `${dayMonth.format(first)} – ${dayMonth.format(last)}`
}

// Attendance % thresholds drive cell/stat color — color follows STATE, not category.
export const ATTENDANCE_GOOD = 90
export const ATTENDANCE_WARN = 75

export function attendanceTone(pct: number | null): string {
  if (pct === null) return 'text-gray-400 dark:text-gray-500'
  if (pct >= ATTENDANCE_GOOD) return 'text-emerald-600 dark:text-emerald-400'
  if (pct >= ATTENDANCE_WARN) return 'text-amber-600 dark:text-amber-400'
  return 'text-rose-600 dark:text-rose-400'
}

export function attendanceBarColor(pct: number): string {
  if (pct >= ATTENDANCE_GOOD) return '#10b981'
  if (pct >= ATTENDANCE_WARN) return '#f59e0b'
  return '#fb7185'
}

export const ROSTER_STATUS_META: Record<
  OrganizationDashboardRosterStatus,
  { label: string; variant: BadgeVariant }
> = {
  present: { label: 'Present', variant: 'success' },
  late: { label: 'Late', variant: 'warning' },
  'half-day': { label: 'Half-day', variant: 'info' },
  'on-leave': { label: 'On leave', variant: 'info' },
  absent: { label: 'Absent', variant: 'danger' },
  scheduled: { label: 'Due later', variant: 'neutral' },
  'not-scheduled': { label: 'Not scheduled', variant: 'neutral' },
}

function csvCell(value: unknown): string {
  const s = String(value ?? '')
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

/** Builds a CSV from a header row and body rows and hands it to the browser. */
export function downloadCsv({
  filename,
  header,
  rows,
  noun,
}: {
  filename: string
  header: string[]
  rows: unknown[][]
  noun: [singular: string, plural: string]
}): void {
  const csv = [header, ...rows].map((line) => line.map(csvCell).join(',')).join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = `${filename}-${new Date().toISOString().slice(0, 10)}.csv`
  anchor.click()
  URL.revokeObjectURL(url)
  toast.success(`Exported ${rows.length} ${rows.length === 1 ? noun[0] : noun[1]}.`)
}
